import {TimeRegistrationEntity} from '../types/TimeRegistrationEntityId';
import type {
	TimeRegistrationModal_companyRolesEntity,
	TimeRegistrationModal_entity,
} from '../types/TimeRegistrationModalEntity';
import type {Role} from '../types/TimeRegistrationRole';

function mapToRole(roleId: string | undefined, roleName: string | undefined | null, disabled = false): Role | null {
	if (roleId && roleName) {
		return {id: roleId, name: roleName, disabled: disabled};
	}
	return null;
}
export function getCompanyRoles(
	companyRoles: TimeRegistrationModal_companyRolesEntity | undefined,
	projectId: string | undefined = undefined,
	selectedRole: Role | undefined = undefined
): Role[] {
	if (companyRoles?.edges) {
		const mappedCompanyRoles = companyRoles.edges.reduce<Role[]>((acc, roleEdge) => {
			const role = mapToRole(roleEdge?.node?.id, roleEdge?.node?.name);
			if (role) {
				acc.push(role);
			}
			return acc;
		}, []);
		if (projectId && selectedRole && !mappedCompanyRoles.find(r => r.id === selectedRole.id)) {
			const mappedRole = mapToRole(selectedRole.id, selectedRole.name, true);
			if (mappedRole) {
				mappedCompanyRoles.push(mappedRole);
			}
		}
		return mappedCompanyRoles;
	} else {
		return [];
	}
}

export function isRoleDisabled(roleId: string | undefined, roles: Role[]): boolean {
	if (roleId === undefined) {
		return false;
	}
	return roles.some(role => role.id === roleId && role.disabled);
}

export function getDisabledRoleIds(roles: Role[]): string[] {
	return roles.filter(role => role.disabled).map(role => role.id);
}

export function getRoleWarningTagOrUndefined(roleId: string | undefined, roles: Role[]): string | undefined {
	return isRoleDisabled(roleId, roles) ? 'project_person.deactivated_role' : undefined;
}
function getProjectPersonRole(targetEntity: TimeRegistrationModal_entity | null): Role | null {
	if (!targetEntity) return null;

	switch (targetEntity.entity) {
		case TimeRegistrationEntity.Task:
			if (targetEntity.project?.projectPerson && targetEntity.project?.projectPerson.role) {
				return targetEntity.project.projectPerson.role as Role;
			} else {
				return null;
			}
		case TimeRegistrationEntity.Project:
			if (targetEntity?.projectPerson?.role) {
				return targetEntity.projectPerson.role as Role;
			} else {
				return null;
			}
		case TimeRegistrationEntity.IdleTime:
			return null;
	}
	return null;
}

/**
 * Get the default role to be used, using this priority list:
 * ProjectPerson.roleId > Task.roleId > Person.roleId > null
 * @param targetEntity
 * @param personRole
 */
export function getDefaultRole(targetEntity: TimeRegistrationModal_entity | null, personRole: Role): Role {
	const projectPersonRole = getProjectPersonRole(targetEntity);
	if (projectPersonRole) {
		return projectPersonRole;
	}

	if (targetEntity?.entity === TimeRegistrationEntity.Task && targetEntity.role) {
		return targetEntity.role as Role;
	}

	return personRole;
}

export type UpdateRoleOnProjectChangeParams = {
	newDefaultRole: Role | undefined;
	initialRole: Role | undefined;
	projectId: string | undefined;
	previousProjectId: string | undefined;
	selectedRole: Role | undefined;
	setSelectedRole: (value: ((prevState: Role | undefined) => Role | undefined) | Role | undefined) => void;
	setShowMovedToProjectRoleWarning: (value: ((prevState: boolean) => boolean) | boolean) => void;
};
export function updateRoleOnProjectChange({
	newDefaultRole,
	initialRole,
	projectId,
	previousProjectId,
	selectedRole,
	setSelectedRole,
	setShowMovedToProjectRoleWarning,
}: UpdateRoleOnProjectChangeParams): void {
	if (
		!!initialRole && // if editing a time reg with a role (internal time is loaded without a role)
		!!projectId && // and we have a project now
		!!previousProjectId && // and we have changed from a different project
		projectId !== previousProjectId && // and we have a project now
		!!newDefaultRole && // and the new task / project combo has a role
		newDefaultRole?.id !== selectedRole?.id // and that role is different from the selected role
	) {
		setSelectedRole(newDefaultRole);
		setShowMovedToProjectRoleWarning(true);
	} else if (initialRole) {
		setShowMovedToProjectRoleWarning(false);
	}
}
